import { CommonModule, DatePipe } from '@angular/common';
import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { Subscription, Observable, combineLatest, from, map, startWith } from 'rxjs';
import { CognitoService } from 'src/app/services/cognito.service';
import { AppState } from 'src/app/state/app.state';
import { TaskComment } from '@workspaces/models/task';
import { taskCommentFeature, TaskCommentFetch } from '@workspaces/store/task-comments.feature';
import { MatInputModule } from '@angular/material/input';
import { WorkspaceTaskService } from '@workspaces/services/task.service';
import { MatButtonModule } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { MatProgressBar } from '@angular/material/progress-bar';
import { ShortIdPipe } from '../../utils/pipes/short-id.pipe';
import { RequiredRoleDirective } from '@workspaces/utils/workspace-guard.directive';
import { workspaceListFeature, WorkspaceFetch } from '@workspaces/store/workspace-list.feature';
import { MatSnackBar } from '@angular/material/snack-bar';

interface EditComment {
  originalText: string;
  comment: TaskComment;
  isEditing: boolean;
  isPending: boolean;
  canBeEdited: boolean;
  canBeDeleted: boolean;
  isSelf: boolean;
}
@Component({
  selector: 'app-task-comments',
  templateUrl: './task-comments.component.html',
  styleUrls: ['./task-comments.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    DatePipe,
    ReactiveFormsModule,
    MatInputModule,
    MatButtonModule,
    MatIcon,
    MatTooltip,
    MatProgressBar,
    ShortIdPipe,
    RequiredRoleDirective,
    MatButtonModule
  ]
})
export class TaskCommentsComponent implements OnInit, OnDestroy {
  private readonly sub = new Subscription();
  @Input({ required: true }) workspaceId!: string;
  @Input({ required: true }) taskId!: string;
  public readonly newCommentControl = new FormControl('', {
    nonNullable: true,
    validators: [Validators.required, Validators.minLength(1), Validators.maxLength(600)]
  });

  constructor(
    private readonly store: Store<AppState>,
    private readonly cognitoService: CognitoService,
    private readonly workspaceTaskService: WorkspaceTaskService,
    private readonly snackbar: MatSnackBar
  ) {}

  public taskComments: EditComment[] = [];
  public taskCommentIsLoading$!: Observable<boolean>;
  public addCommentIsLoading = false;
  public isDisabledEdit = false;
  async ngOnInit() {
    this.sub.add(
      combineLatest([
        this.store.select(taskCommentFeature.taskCommentData(this.taskId)),
        from(this.cognitoService.isAdmin()),
        from(this.cognitoService.getUserId())
      ]).subscribe(([comments, isAdmin, currentUserId]) => {
        this.taskComments = structuredClone(comments).map(comment => ({
          comment: { ...comment, created_at: comment.created_at * 1000 }, //To be able to safely pipe
          isEditing: false,
          originalText: comment.comment,
          isPending: false,
          canBeEdited: comment.created_by.id === currentUserId,
          canBeDeleted: isAdmin || comment.created_by.id === currentUserId,
          isSelf: comment.created_by.id === currentUserId
        }));
      })
    );
    this.taskCommentIsLoading$ = this.store.select(taskCommentFeature.taskCommentIsLoading(this.taskId));
    this.sub.add(
      this.store
        .select(workspaceListFeature.workspaceById(this.workspaceId))
        .pipe(
          map(workspace => !!workspace?.is_archived || !!workspace?.is_deleted),
          startWith(false)
        )
        .subscribe(isDisabledEdit => {
          if (isDisabledEdit) {
            this.newCommentControl.disable();
          } else {
            this.newCommentControl.enable();
          }
          this.isDisabledEdit = isDisabledEdit;
        })
    );
    this.dispatchInitialDataActions();
  }

  private dispatchInitialDataActions() {
    this.store.dispatch(WorkspaceFetch({ workspaceId: this.workspaceId }));

    this.store.dispatch(TaskCommentFetch({ workspaceId: this.workspaceId, taskId: this.taskId }));
  }

  public addComment(): void {
    if (this.newCommentControl.invalid) {
      this.newCommentControl.markAllAsTouched();
      return;
    }

    const commentText = this.newCommentControl.value;
    this.addCommentIsLoading = true;

    this.sub.add(
      this.workspaceTaskService
        .postComment(this.workspaceId, this.taskId, { content: commentText })
        .subscribe({
          next: () => {
            this.store.dispatch(TaskCommentFetch({ workspaceId: this.workspaceId, taskId: this.taskId }));
            this.newCommentControl.reset();
            this.snackbar.open('Comment was added!', 'Dismiss', {
              duration: 5000,
              panelClass: ['success-snackbar']
            });
          }
        })
        .add(() => {
          this.addCommentIsLoading = false;
        })
    );
  }

  public toggleEditMode(editComment: EditComment, isEditing: boolean): void {
    this.taskComments.forEach(comment => {
      comment.isEditing = false;
      comment.comment.comment = comment.originalText;
    });
    editComment.isEditing = isEditing;
    if (!isEditing) {
      editComment.comment.comment = editComment.originalText;
    }
  }

  public updateComment(editComment: EditComment) {
    editComment.isPending = true;
    this.sub.add(
      this.workspaceTaskService
        .updateComment(this.workspaceId, this.taskId, editComment.comment.comment_id, {
          content: editComment.comment.comment
        })
        .subscribe({
          error: () => {
            editComment.isPending = false;
          },
          next: () => {
            editComment.isPending = false;
            editComment.isEditing = false;
            this.store.dispatch(TaskCommentFetch({ workspaceId: this.workspaceId, taskId: this.taskId }));
            this.snackbar.open('Comment was updated!', 'Dismiss', {
              duration: 5000,
              panelClass: ['success-snackbar']
            });
          }
        })
    );
  }
  public deleteComment(editComment: EditComment) {
    editComment.isPending = true;
    this.sub.add(
      this.workspaceTaskService.deleteComment(this.workspaceId, this.taskId, editComment.comment.comment_id).subscribe({
        error: () => {
          editComment.isPending = false;
        },
        next: () => {
          editComment.isPending = false;
          this.snackbar.open('Comment was deleted!', 'Dismiss', {
            duration: 5000,
            panelClass: ['success-snackbar']
          });
          this.store.dispatch(TaskCommentFetch({ workspaceId: this.workspaceId, taskId: this.taskId }));
        }
      })
    );
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }
}
